import safeStorage from '@github-ui/safe-storage'

export interface PullRequestSubmissionContext {
  userId: string
  repositoryId: string
  headRepositoryId: string
  baseRef: string
  headRef: string
}

export interface PullRequestSubmissionFields {
  title: string
  body: string
}

export interface PullRequestSubmissionRecovery {
  id: string
  context: PullRequestSubmissionContext
}

interface Submission extends PullRequestSubmissionFields {
  context: PullRequestSubmissionContext
  expiresAt: number
}

const prefix = 'pull-request-submission:'
const lifetime = 60 * 60 * 1000
const storage = safeStorage('sessionStorage')
const contextKeys = ['userId', 'repositoryId', 'headRepositoryId', 'baseRef', 'headRef'] as const

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function isPullRequestSubmissionContext(value: unknown): value is PullRequestSubmissionContext {
  return isRecord(value) && contextKeys.every(field => typeof value[field] === 'string')
}

function isSubmission(value: unknown): value is Submission {
  return (
    isRecord(value) &&
    isPullRequestSubmissionContext(value.context) &&
    typeof value.title === 'string' &&
    typeof value.body === 'string' &&
    typeof value.expiresAt === 'number' &&
    Number.isFinite(value.expiresAt)
  )
}

function submissionKey(id: string): string | null {
  return /^[a-f0-9]{32}$/.test(id) ? `${prefix}${id}` : null
}

export function clearPullRequestSubmission(id: string): void {
  const key = submissionKey(id)
  if (key) storage.removeItem(key)
}

function readSubmission(key: string): Submission | null {
  const raw = storage.getItem(key)
  if (raw === null) return null

  let value: unknown
  try {
    value = JSON.parse(raw)
  } catch (error) {
    if (!(error instanceof SyntaxError)) throw error
    storage.removeItem(key)
    return null
  }

  if (!isSubmission(value) || value.expiresAt <= Date.now()) {
    storage.removeItem(key)
    return null
  }
  return value
}

/** Only explicit recovery requests may read these snapshots; ordinary visits must not restore them. */
export function readPullRequestSubmission({
  id,
  context,
}: PullRequestSubmissionRecovery): PullRequestSubmissionFields | null {
  const key = submissionKey(id)
  const submission = key ? readSubmission(key) : null
  if (!submission || !contextKeys.every(field => submission.context[field] === context[field])) return null
  return {title: submission.title, body: submission.body}
}

export function updatePullRequestSubmission(
  recovery: PullRequestSubmissionRecovery,
  fields: Partial<PullRequestSubmissionFields>,
): void {
  const submission = readPullRequestSubmission(recovery)
  if (!submission) return
  savePullRequestSubmission(recovery, {
    title: fields.title ?? submission.title,
    body: fields.body ?? submission.body,
  })
}

/** Best-effort persistence must not block native form submission when storage is unavailable. */
export function savePullRequestSubmission(
  {id, context}: PullRequestSubmissionRecovery,
  fields: PullRequestSubmissionFields,
): void {
  const key = submissionKey(id)
  if (!key) return

  for (const oldKey of storage.getKeys()) {
    if (oldKey !== key && oldKey.startsWith(prefix)) readSubmission(oldKey)
  }

  const serialized = JSON.stringify({...fields, context, expiresAt: Date.now() + lifetime})
  storage.removeItem(key)
  storage.setItem(key, serialized)
}
